import axios from 'axios';
import {
    EventBus
} from '@/eventBus';
import { GET_KEEP_ALIVE } from '@/constants/api';

export default function (Vue) {
    const LOCAL_EXPIRATION_RECHECK_COOLDOWN_MS = 5000;
    const LOCAL_EXPIRATION_RECHECK_GRACE_SECONDS = 60;
    let expirationRecheckPromise = null;
    let expirationRecheckToken = null;
    let expirationRecheckStartedAt = 0;
    let expirationGraceUntil = 0;

    const hasValidUserId = (userId) => {
        if (userId === null || typeof userId === 'undefined') {
            return false;
        }

        const normalizedUserId = String(userId).trim().toLowerCase();

        return normalizedUserId !== '' && normalizedUserId !== 'null' && normalizedUserId !== 'undefined';
    };

    const sanitizeCabinetLogToken = (value) => {
        if (value === null || typeof value === 'undefined') {
            return null;
        }

        return String(value)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "") || null;
    };

    const getAuthSnapshot = () => {
        const expiration = localStorage.getItem('expiration');
        const username = localStorage.getItem('username');
        const userId = localStorage.getItem('user_id');

        return {
            expiration: expiration ? Number(expiration) || null : null,
            requestType: localStorage.getItem('request_type') || null,
            tokenExists: Boolean(localStorage.getItem('token')),
            userId: hasValidUserId(userId) ? String(userId) : null,
            username: username || null,
        };
    };

    const emitCabinetAuthLog = (eventName, auth = {}) => {
        const snapshot = getAuthSnapshot();

        EventBus.$emit("cabinet-log", {
            auth: {
                expiration: snapshot.expiration,
                requestType: snapshot.requestType,
                tokenExists: snapshot.tokenExists,
                ...auth,
            },
            createdAt: new Date().toISOString(),
            eventName,
            orderId: null,
            phoneState: null,
            source: "auth",
            userId: snapshot.userId,
            username: snapshot.username,
        });
    };

    const buildExpirationRecheckUrl = () => {
        const baseURL = axios.defaults.baseURL || process.env.VUE_APP_API_HOST || "";

        if (!baseURL) {
            return GET_KEEP_ALIVE;
        }

        return `${String(baseURL).replace(/\/+$/, "")}/${GET_KEEP_ALIVE.replace(/^\/+/, "")}`;
    };

    const buildExpirationRecheckHeaders = (token) => {
        const headers = {
            Authorization: `Basic ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Data-Access-Type': localStorage.getItem('type') || '',
            'X-Data-Username': localStorage.getItem('username') || '',
            'X-Data-Request-Type': localStorage.getItem('request_type') || 'cabinet',
        };
        const userId = localStorage.getItem('user_id');
        const onlineToken = window.sessionStorage.getItem('online-token');

        if (hasValidUserId(userId)) {
            headers['X-User-Id'] = String(userId);
        }

        if (onlineToken) {
            headers['X-Online-Token'] = onlineToken;
        }

        return headers;
    };

    const performExpirationRecheck = async (auth, payload) => {
        const {
            expiration,
            now,
            token,
        } = payload;

        emitCabinetAuthLog('auth.expiration_recheck.started', {
            reason: 'local_expired',
            staleExpiration: expiration,
            now,
        });

        let response;
        try {
            response = await window.fetch(buildExpirationRecheckUrl(), {
                headers: buildExpirationRecheckHeaders(token),
                method: 'GET',
            });
        } catch (error) {
            expirationGraceUntil = Math.max(
                expirationGraceUntil,
                Math.round(Date.now() / 1000) + LOCAL_EXPIRATION_RECHECK_GRACE_SECONDS
            );
            emitCabinetAuthLog('auth.expiration_recheck.network_error', {
                message: error?.message || 'network_error',
                staleExpiration: expiration,
            });
            return false;
        }

        if (localStorage.getItem('token') !== token) {
            emitCabinetAuthLog('auth.expiration_recheck.skipped.token_changed', {
                staleExpiration: expiration,
                status: response.status,
            });
            return false;
        }

        if (response.status === 401) {
            emitCabinetAuthLog('auth.expiration_recheck.http_401', {
                staleExpiration: expiration,
                status: response.status,
            });
            auth.destroyToken('local_expired_recheck_401', {
                expiration,
                now: Math.round(Date.now() / 1000),
                status: response.status,
            });
            return false;
        }

        if (!response.ok) {
            expirationGraceUntil = Math.max(
                expirationGraceUntil,
                Math.round(Date.now() / 1000) + LOCAL_EXPIRATION_RECHECK_GRACE_SECONDS
            );
            emitCabinetAuthLog('auth.expiration_recheck.http_error', {
                staleExpiration: expiration,
                status: response.status,
            });
            return false;
        }

        const nextToken = response.headers.get('X-Auth-Token');
        const nextExpirationRaw = response.headers.get('X-Api-Token-Expires');
        const nextExpiration = nextExpirationRaw ? Number(nextExpirationRaw) || null : null;
        const nextUserId = response.headers.get('X-User-Id');
        const previousToken = localStorage.getItem('token');

        if (nextToken) {
            auth.setToken(nextToken, nextExpiration);

            if (previousToken && previousToken !== nextToken) {
                EventBus.$emit('auth-token-updated', nextToken);
            }
        } else if (nextExpiration) {
            auth.updateTokenExpiration(nextExpiration);
        } else {
            expirationGraceUntil = Math.max(
                expirationGraceUntil,
                Math.round(Date.now() / 1000) + LOCAL_EXPIRATION_RECHECK_GRACE_SECONDS
            );
        }

        if (hasValidUserId(nextUserId)) {
            localStorage.setItem('user_id', String(nextUserId));
        }

        emitCabinetAuthLog('auth.expiration_recheck.recovered', {
            nextExpiration,
            status: response.status,
            tokenRotated: Boolean(nextToken && previousToken && previousToken !== nextToken),
            visibleHeaders: {
                expiration: Boolean(nextExpirationRaw),
                token: Boolean(nextToken),
                userId: hasValidUserId(nextUserId),
            },
        });

        return true;
    };

    Vue.auth = {
        setToken(token, expiration = null) {
            localStorage.setItem('token', token);

            let nextExpiration = expiration;
            if (!nextExpiration) {
                let tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                nextExpiration = Math.round(+tomorrow / 1000);
            }

            localStorage.setItem('expiration', String(nextExpiration));
        },

        updateTokenExpiration(expiration) {
            if (!expiration || !localStorage.getItem('token')) {
                return;
            }

            localStorage.setItem('expiration', String(expiration));
        },

        setUser(username, type, userId) {
            localStorage.setItem('username', username);
            localStorage.setItem('type', type);

            if (hasValidUserId(userId)) {
                localStorage.setItem('user_id', String(userId));
                return;
            }

            localStorage.removeItem('user_id');
        },

        setRequestType(type) {
            localStorage.setItem('request_type', type)
        },

        getRequestType() {
           let type = localStorage.getItem('request_type');
           if (!type) {
               return null;
           }

           return type;
        },

        setSessionId(session_id) {
            document.cookie = `session_id=${session_id}`;
            localStorage.setItem('session_id', session_id);
        },

        getUser() {
            return {
                username: localStorage.getItem('username'),
                type: localStorage.getItem('type'),
                user_id: localStorage.getItem('user_id'),
            }
        },

        getToken() {
            let token = localStorage.getItem('token');
            let expiration = localStorage.getItem('expiration');
            const now = Math.round(new Date().getTime() / 1000);

            if (!token || !expiration) return null;
            if (now <= expirationGraceUntil) {
                return token;
            }

            if (now > expiration) {
                this.requestExpirationRecheck({
                    expiration: Number(expiration) || null,
                    now,
                    token,
                });
                return token;
            }

            return token;
        },

        requestExpirationRecheck(payload) {
            if (!payload?.token) {
                return Promise.resolve(false);
            }

            const shouldReusePendingPromise = expirationRecheckPromise
                && expirationRecheckToken === payload.token;
            if (shouldReusePendingPromise) {
                return expirationRecheckPromise;
            }

            const isCooldownWindow = expirationRecheckToken === payload.token
                && (Date.now() - expirationRecheckStartedAt) < LOCAL_EXPIRATION_RECHECK_COOLDOWN_MS;
            if (isCooldownWindow) {
                return Promise.resolve(false);
            }

            expirationRecheckToken = payload.token;
            expirationRecheckStartedAt = Date.now();
            expirationRecheckPromise = performExpirationRecheck(this, payload)
                .finally(() => {
                    expirationRecheckPromise = null;
                });

            return expirationRecheckPromise;
        },

        destroyToken(reason = "unknown", details = {}) {
            const hadAuthState = Boolean(
                localStorage.getItem('token')
                || localStorage.getItem('expiration')
                || localStorage.getItem('request_type')
            );
            const normalizedReason = sanitizeCabinetLogToken(reason) || "unknown";
            const snapshot = getAuthSnapshot();

            expirationRecheckPromise = null;
            expirationRecheckToken = null;
            expirationRecheckStartedAt = 0;
            expirationGraceUntil = 0;

            if (hadAuthState) {
                emitCabinetAuthLog(`auth.destroy_token.${normalizedReason}`, {
                    expiration: snapshot.expiration,
                    reason: normalizedReason,
                    ...details,
                });
            }

            localStorage.removeItem('token');
            localStorage.removeItem('expiration');
            localStorage.removeItem('request_type')

            if (hadAuthState) {
                EventBus.$emit('logout-user');
            }
        },

        isAuthenticated() {
            return this.getToken() !== null;
        },
        getSessionId() {
            return localStorage.getItem('session_id');
        },
        hasAutoLoginParams(query) {
            return Object.keys(query).length !== 0 &&
                typeof query.username !== 'undefined' &&
                typeof query.api_token !== 'undefined';
        },
        isAutoLogin() {
            let urlParams = new URLSearchParams(window.location.search);
            return urlParams.has('username') &&
                urlParams.has('api_token') &&
                urlParams.has('api_token_expired');
        },
        isAutoLoginDoNotMatch() {
            const {
                username
            } = this.getUser();
            const token = localStorage.getItem('token');
            const expiration = localStorage.getItem('expiration');
            const urlParams = new URLSearchParams(window.location.search);

            return this.isAutoLogin() && (
                username !== urlParams.get('username') ||
                token !== urlParams.get('api_token') ||
                expiration !== urlParams.get('api_token_expired')
            )
        },
    };

    Object.defineProperties(Vue.prototype, {
        $auth: {
            get() {
                return Vue.auth;
            }
        }
    })
}
